import React, { forwardRef, type ReactNode, type RefObject } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Dialog as PrimitiveDialog,
  DialogContent as PrimitiveDialogContent,
  DialogDescription,
  DialogFooter as PrimitiveDialogFooter,
  DialogHeader as PrimitiveDialogHeader,
  DialogTitle as PrimitiveDialogTitle,
} from "src/primitives/Dialog";

import { SIZE_CLASS_MAP } from "./constants";

export type DialogSize = "small" | "medium" | "large" | "fullScreen";

/** Radix Dialog.Content props we forward via ...otherProps. */
type DialogContentProps = React.ComponentProps<typeof PrimitiveDialogContent>;

export interface DialogProps extends Omit<
  DialogContentProps,
  | "children"
  | "className"
  | "showCloseButton"
  | "onEscapeKeyDown"
  | "onInteractOutside"
> {
  /** Size of the dialog. */
  size?: DialogSize;
  /** Whether the dialog is open. */
  isOpen?: boolean;
  /** Callback invoked when the dialog is closed. */
  onClose?: () => void;
  /** Content rendered inside the dialog. */
  children?: ReactNode;
  /** Additional CSS class names applied to the dialog content. */
  className?: string;
  /** Close on pressing the Esc key. */
  closeOnEsc?: boolean;
  /** Show the close button. */
  closeButton?: boolean;
  /** Additional CSS class names applied to the backdrop/overlay. */
  backdropClassName?: string;
  /** Close on clicking outside the dialog. */
  closeOnOutsideClick?: boolean;
  /** Ref of the element to receive focus when the dialog opens. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Ref of the element to receive focus when the dialog closes. */
  finalFocusRef?: RefObject<HTMLElement | null>;
  /** Whether to block body scroll when the dialog is mounted. Maps to Radix `modal` prop. */
  blockScrollOnMount?: boolean;
  /** Force children to re-render even when the dialog is closed. */
  forceRender?: boolean;
  /** Radix Dialog `modal` prop — takes precedence over blockScrollOnMount. */
  modal?: boolean;
  /** Radix Dialog `defaultOpen` prop. */
  defaultOpen?: boolean;
}

interface DialogSubcomponentProps extends React.ComponentProps<"div"> {
  children?: ReactNode;
  className?: string;
  /** Custom data-testid attribute. */
  dataTestid?: string;
}

const DialogHeader = forwardRef<
  HTMLDivElement,
  DialogSubcomponentProps & { description?: string }
>(({ children, className, description, dataTestid, ...otherProps }, ref) => (
  <PrimitiveDialogHeader
    ref={ref}
    className={cn(className)}
    data-testid={dataTestid ?? "dialog-header"}
    {...otherProps}
  >
    {children}
    {description && <DialogDescription>{description}</DialogDescription>}
  </PrimitiveDialogHeader>
));
DialogHeader.displayName = "Dialog.Header";

const DialogBody = forwardRef<HTMLDivElement, DialogSubcomponentProps>(
  ({ children, className, dataTestid, ...otherProps }, ref) => (
    <div
      ref={ref}
      data-slot="dialog-body"
      data-testid={dataTestid ?? "dialog-body"}
      className={cn("text-sm text-foreground", className)}
      {...otherProps}
    >
      {children}
    </div>
  )
);
DialogBody.displayName = "Dialog.Body";

const DialogFooter = forwardRef<HTMLDivElement, DialogSubcomponentProps>(
  ({ children, className, dataTestid, ...otherProps }, ref) => (
    <PrimitiveDialogFooter
      ref={ref}
      className={cn(className)}
      data-testid={dataTestid ?? "dialog-footer"}
      {...otherProps}
    >
      {children}
    </PrimitiveDialogFooter>
  )
);
DialogFooter.displayName = "Dialog.Footer";

const DialogTitle = forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof PrimitiveDialogTitle>
>(({ children, className, ...otherProps }, ref) => (
  <PrimitiveDialogTitle
    ref={ref}
    className={cn("text-2xl font-semibold", className)}
    {...otherProps}
  >
    {children}
  </PrimitiveDialogTitle>
));
DialogTitle.displayName = "Dialog.Title";

const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      size = "medium",
      isOpen = false,
      onClose = () => {},
      children,
      className,
      closeOnEsc = true,
      closeButton = true,
      backdropClassName,
      closeOnOutsideClick = true,
      initialFocusRef,
      finalFocusRef,
      blockScrollOnMount = true,
      forceRender = false,
      modal: modalProp,
      defaultOpen,
      // Radix content handlers — intercept and merge
      onOpenAutoFocus: onOpenAutoFocusProp,
      onCloseAutoFocus: onCloseAutoFocusProp,
      ...otherContentProps
    },
    ref
  ) => {
    const handleOpenChange = (open: boolean) => {
      if (!open) onClose();
    };

    const handleInteractOutside = (e: Event) => {
      if (!closeOnOutsideClick) e.preventDefault();
    };

    const handleEscapeKeyDown = (e: KeyboardEvent) => {
      if (!closeOnEsc) e.preventDefault();
    };

    const handleOpenAutoFocus = (e: Event) => {
      if (initialFocusRef?.current) {
        e.preventDefault();
        initialFocusRef.current.focus();
      }
      onOpenAutoFocusProp?.(e);
    };

    const handleCloseAutoFocus = (e: Event) => {
      if (finalFocusRef?.current) {
        e.preventDefault();
        finalFocusRef.current.focus();
      }
      onCloseAutoFocusProp?.(e);
    };

    // `modal` prop takes precedence; fall back to blockScrollOnMount
    const resolvedModal = modalProp ?? blockScrollOnMount;

    return (
      <PrimitiveDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={resolvedModal}
        defaultOpen={defaultOpen}
      >
        <PrimitiveDialogContent
          ref={ref}
          aria-modal
          showCloseButton={closeButton}
          forceMount={forceRender || undefined}
          overlayClassName={backdropClassName}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          className={cn(SIZE_CLASS_MAP[size], className)}
          data-testid="dialog-wrapper"
          {...otherContentProps}
        >
          {children}
        </PrimitiveDialogContent>
      </PrimitiveDialog>
    );
  }
) as React.ForwardRefExoticComponent<
  DialogProps & React.RefAttributes<HTMLDivElement>
> & {
  Header: typeof DialogHeader;
  Body: typeof DialogBody;
  Footer: typeof DialogFooter;
  Title: typeof DialogTitle;
};

Dialog.displayName = "Dialog";
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;

export { Dialog };
